import { useEffect, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FullScreenSpinner } from '../../components/ui/Spinner';
import EmptyState from '../../components/ui/EmptyState';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import DatePicker from '../../components/ui/DatePicker';
import { StatusBadge } from '../../components/ui/Badge';
import CategoryBadge from '../../components/candidates/CategoryBadge';
import StepProgress from '../../components/candidates/StepProgress';
import {
  StepIconRow,
  StepDetailPanel,
  useSelectedStep,
  formatAnswer,
  needsConversionReview,
  missingConversionDetails,
} from '../../components/candidates/StepTimeline';
import { IconClose } from '../../components/ui/icons';
import DocumentsTab from '../../components/candidates/DocumentsTab';
import CandidateProfileTab from '../../components/candidates/CandidateProfileTab';
import SlaTab from '../../components/candidates/SlaTab';
import ActivityTab from '../../components/candidates/ActivityTab';
import RegistrationCompleteBanner from '../../components/candidates/RegistrationCompleteBanner';
import WithdrawDialog from '../../components/candidates/WithdrawDialog';
import ChangeCategoryModal from '../../components/candidates/ChangeCategoryModal';
import ConvertVehicleTypeModal, { targetVehicleCategory } from '../../components/candidates/ConvertVehicleTypeModal';
import Tabs from '../../components/ui/Tabs';
import Modal from '../../components/ui/Modal';
import Button from '../../components/ui/Button';
import Can from '../../auth/Can';
import ProfilePhoto from '../../components/candidates/ProfilePhoto';
import { usePermission } from '../../auth/usePermission';
import { getCandidate, listCandidates, withdrawCandidate, reopenCandidate } from '../../api/candidates';
import { listWorkflowCategories, listStepDefinitions } from '../../api/workflow';
import { pushToast } from '../../components/ui/toastBus';

const CANDIDATE_STATUS_OPTIONS = [
  { value: 'registered', label: 'Registered' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'hired', label: 'Hired' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'withdrawn', label: 'Backout' },
  { value: 'hold', label: 'On Hold' },
];

// The same filter fields as the /candidates list page, but scoped to a small collapsible
// panel so a user viewing one candidate can search/filter to jump straight to another one.
function CandidateFilterPanel() {
  const [open, setOpen] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [currentStep, setCurrentStep] = useState('');
  const [createdFrom, setCreatedFrom] = useState('');
  const [createdTo, setCreatedTo] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const categoriesQuery = useQuery({ queryKey: ['workflow-categories'], queryFn: listWorkflowCategories, enabled: open });
  const stepDefinitionsQuery = useQuery({ queryKey: ['step-definitions'], queryFn: listStepDefinitions, enabled: open });

  const params = {
    search: search || undefined,
    status: status || undefined,
    workflow_category_id: categoryId || undefined,
    current_step: currentStep || undefined,
    created_from: createdFrom || undefined,
    created_to: createdTo || undefined,
    per_page: 8,
  };

  const resultsQuery = useQuery({
    queryKey: ['candidates-switcher', params],
    queryFn: () => listCandidates(params),
    enabled: open,
  });

  const results = resultsQuery.data?.data || [];

  return (
    <div className="mb-4">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="mb-2 inline-flex items-center gap-1 text-sm font-medium text-gray-600 hover:text-gray-900"
      >
        Filter candidates {open ? '▲' : '▼'}
      </button>

      {open && (
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <div className="flex flex-wrap gap-3">
            <Input
              placeholder="Search name, reference, passport, contact…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="w-64"
            />
            <Select value={status} onChange={(e) => setStatus(e.target.value)} className="w-40">
              <option value="">Any status</option>
              {CANDIDATE_STATUS_OPTIONS.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </Select>
            <Select value={categoryId} onChange={(e) => setCategoryId(e.target.value)} className="w-48">
              <option value="">All categories</option>
              {(categoriesQuery.data || []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
            <Select value={currentStep} onChange={(e) => setCurrentStep(e.target.value)} className="w-48">
              <option value="">Any current step</option>
              {(stepDefinitionsQuery.data || []).map((s) => (
                <option key={s.code} value={s.code}>
                  {s.title}
                </option>
              ))}
            </Select>
            <DatePicker value={createdFrom} onChange={(e) => setCreatedFrom(e.target.value)} className="w-36" />
            <DatePicker value={createdTo} onChange={(e) => setCreatedTo(e.target.value)} className="w-36" />
          </div>

          <div className="mt-3 divide-y divide-gray-100 border-t border-gray-100">
            {resultsQuery.isLoading && <p className="py-3 text-sm text-gray-400">Loading…</p>}
            {!resultsQuery.isLoading && results.length === 0 && (
              <p className="py-3 text-sm text-gray-400">No candidates match.</p>
            )}
            {results.map((row) => (
              <Link
                key={row.id}
                to={`/candidates/${row.id}`}
                className="flex items-center justify-between gap-3 py-2 text-sm hover:bg-gray-50"
              >
                <div className="min-w-0">
                  <span className="font-medium text-primary">{row.reference_no}</span>{' '}
                  <span className="text-gray-700">{row.full_name}</span>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <CategoryBadge category={row.category} />
                  <StatusBadge status={row.status} />
                </div>
              </Link>
            ))}
            {resultsQuery.data?.total > results.length && (
              <p className="pt-2 text-xs text-gray-400">
                Showing {results.length} of {resultsQuery.data.total} — refine filters to narrow down.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const TABS = [
  { value: 'documents', label: 'Documents' },
  { value: 'profile', label: 'Profile' },
  { value: 'activity', label: 'Activity' },
  { value: 'sla', label: 'SLA' },
];

const TERMINAL_STATUSES = ['rejected', 'withdrawn', 'hired', 'hold', 'suspended'];

function OverflowMenu({ candidate }) {
  const [open, setOpen] = useState(false);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [changeCategoryOpen, setChangeCategoryOpen] = useState(false);
  const [convertOpen, setConvertOpen] = useState(false);
  const menuRef = useRef(null);
  const queryClient = useQueryClient();
  const categoriesQuery = useQuery({ queryKey: ['workflow-categories'], queryFn: listWorkflowCategories });
  const convertTarget = targetVehicleCategory(candidate.category, categoriesQuery.data);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['candidates', String(candidate.id)] });

  const withdrawMutation = useMutation({
    mutationFn: (reason) => withdrawCandidate(candidate.id, reason),
    onSuccess: () => {
      invalidate();
      pushToast({ type: 'success', message: 'Candidate marked as backout.' });
      setWithdrawOpen(false);
    },
    onError: (err) => pushToast({ type: 'error', message: err.response?.data?.message || 'Could not mark as backout.' }),
  });

  const reopenMutation = useMutation({
    mutationFn: () => reopenCandidate(candidate.id),
    onSuccess: () => {
      invalidate();
      pushToast({ type: 'success', message: 'Candidate reopened.' });
    },
    onError: (err) => pushToast({ type: 'error', message: err.response?.data?.message || 'Could not reopen.' }),
  });

  const canChangeCategory = candidate.steps.every((s) => s.code === 'registration' || s.status === 'pending');

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50"
      >
        Actions ▾
      </button>

      {open && (
        <div className="absolute right-0 z-10 mt-2 w-56 rounded-md border border-gray-200 bg-white py-1 shadow-lg">
          <Can perm="candidates.withdraw">
            {!TERMINAL_STATUSES.includes(candidate.status) && (
              <button
                onClick={() => {
                  setOpen(false);
                  setWithdrawOpen(true);
                }}
                className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
              >
                Backout
              </button>
            )}
          </Can>
          <Can perm="candidates.reopen">
            {(candidate.status === 'withdrawn' || candidate.status === 'rejected') && (
              <button
                onClick={() => {
                  setOpen(false);
                  reopenMutation.mutate();
                }}
                className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
              >
                Reopen
              </button>
            )}
          </Can>
          <Can perm="candidates.change-category">
            {canChangeCategory && (
              <button
                onClick={() => {
                  setOpen(false);
                  setChangeCategoryOpen(true);
                }}
                className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
              >
                Change category
              </button>
            )}
          </Can>
          <Can perm="candidates.convert-vehicle-type">
            {!TERMINAL_STATUSES.includes(candidate.status) && convertTarget && (
              <button
                onClick={() => {
                  setOpen(false);
                  setConvertOpen(true);
                }}
                className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
              >
                Convert to {candidate.category.vehicle_type === 'taxi' ? 'Bike' : 'Taxi'}
              </button>
            )}
          </Can>
        </div>
      )}

      <WithdrawDialog
        open={withdrawOpen}
        onClose={() => setWithdrawOpen(false)}
        onConfirm={(reason) => withdrawMutation.mutate(reason)}
        loading={withdrawMutation.isPending}
      />
      <ChangeCategoryModal open={changeCategoryOpen} onClose={() => setChangeCategoryOpen(false)} candidate={candidate} />
      <ConvertVehicleTypeModal
        open={convertOpen}
        onClose={() => setConvertOpen(false)}
        candidate={candidate}
        categories={categoriesQuery.data}
      />
    </div>
  );
}

export default function CandidateWorkspace() {
  const { id } = useParams();
  const [tab, setTab] = useState('documents');

  const candidateQuery = useQuery({ queryKey: ['candidates', id], queryFn: () => getCandidate(id) });

  if (candidateQuery.isLoading) return <FullScreenSpinner />;
  if (candidateQuery.isError) return <EmptyState title="Candidate not found" />;

  const candidate = candidateQuery.data;

  return <CandidateWorkspaceContent candidate={candidate} tab={tab} setTab={setTab} />;
}

function hasAnswer(value) {
  return Array.isArray(value) ? value.length > 0 : value !== undefined && value !== null && value !== '';
}

function CandidateWorkspaceContent({ candidate, tab, setTab }) {
  const { can } = usePermission();
  const [selectedStep, setSelectedStepId] = useSelectedStep(candidate);
  const [stepModalOpen, setStepModalOpen] = useState(false);

  const openStep = (candidateStepId) => {
    setSelectedStepId(candidateStepId);
    setStepModalOpen(true);
  };

  // Deep link from Assigned Steps (?step=<candidate_step_id>): open that step's panel once.
  const [searchParams, setSearchParams] = useSearchParams();
  const linkedStep = Number(searchParams.get('step')) || null;
  useEffect(() => {
    if (!linkedStep) return;
    if (candidate.steps.some((s) => s.candidate_step_id === linkedStep)) openStep(linkedStep);
    setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedStep]);

  const basicDetailFields = (candidate.steps || []).flatMap((step) =>
    (step.fields || [])
      .filter((f) => f.show_in_basic_details && hasAnswer(step.answers?.[f.id]))
      .map((f) => ({ key: `${step.workflow_step_id}:${f.id}`, label: f.label, value: formatAnswer(step.answers[f.id]) }))
  );

  const overdueStep = (candidate.steps || []).find((s) => s.is_overdue && (s.is_current || s.linked_sla?.is_overdue));
  const conversionReviewSteps = (candidate.steps || []).filter(needsConversionReview);

  return (
    <div>
      <CandidateFilterPanel />
      <div className="mb-6 rounded-lg border border-gray-200 bg-white p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex min-w-0 items-center gap-4">
            <ProfilePhoto
              kind="candidates"
              id={candidate.id}
              hasPhoto={candidate.has_profile_photo}
              name={candidate.full_name}
              editable={can('candidates.update')}
            />
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-lg font-semibold text-gray-900">{candidate.full_name}</h1>
                <StatusBadge status={candidate.status} />
                <CategoryBadge category={candidate.category} />
              </div>
              <p className="mt-0.5 text-xs text-gray-500">{candidate.reference_no}</p>
              {candidate.progress.current_sort_order && (
                <div className="mt-2 w-48">
                  <StepProgress current={candidate.progress.current_sort_order} total={candidate.progress.total_steps} />
                </div>
              )}
            </div>
          </div>
          <OverflowMenu candidate={candidate} />
        </div>

        {candidate.status === 'rejected' && (
          <div className="mt-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
            Recruitment closed — rejected. {candidate.rejection_reason}
          </div>
        )}
        {candidate.status === 'withdrawn' && (
          <div className="mt-3 rounded-md bg-gray-100 px-3 py-2 text-sm text-gray-700">
            Backout. {candidate.rejection_reason}
          </div>
        )}
        {(candidate.status === 'hold' || candidate.status === 'suspended') && (
          <div className="mt-3 rounded-md bg-purple-50 px-3 py-2 text-sm text-purple-800">
            {candidate.status === 'hold' ? 'On hold' : 'Suspended'} — no changes can be made until it is reverted. {candidate.rejection_reason}
          </div>
        )}
        {candidate.status === 'hired' && (
          <div className="mt-3 rounded-md bg-green-50 px-3 py-2 text-sm text-green-700">
            Hired{candidate.employee_no ? ` — Employee No ${candidate.employee_no}` : ''}.
          </div>
        )}

        <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-2 border-t border-gray-100 pt-3 text-sm">
          <div>
            <span className="text-xs uppercase tracking-wide text-gray-400">Nationality</span>{' '}
            <span className="text-gray-800">{candidate.nationality}</span>
          </div>
          <div>
            <span className="text-xs uppercase tracking-wide text-gray-400">Passport No</span>{' '}
            <span className="text-gray-800">{candidate.passport_no}</span>
          </div>
          <div>
            <span className="text-xs uppercase tracking-wide text-gray-400">Contact No</span>{' '}
            <span className="text-gray-800">{candidate.contact_no}</span>
          </div>
          {candidate.employee_no && (
            <div>
              <span className="text-xs uppercase tracking-wide text-gray-400">Employee No</span>{' '}
              <span className="text-gray-800">{candidate.employee_no}</span>
            </div>
          )}
          {basicDetailFields.map((f) => (
            <div key={f.key}>
              <span className="text-xs uppercase tracking-wide text-gray-400">{f.label}</span>{' '}
              <span className="text-gray-800">{f.value}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="mb-6 grid grid-cols-1 gap-6 lg:grid-cols-3 lg:items-stretch">
        <div className="flex flex-col rounded-lg border border-gray-200 bg-white p-6 lg:col-span-2">
          <h2 className="mb-4 text-sm font-semibold text-gray-900">Workflow Steps</h2>
          <RegistrationCompleteBanner candidate={candidate} />
          {conversionReviewSteps.length > 0 && (
            <div className="mb-4 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-800">
              <span className="font-semibold">Needs review after conversion</span> — carried over but still missing what
              the new workflow requires:
              <ul className="mt-1 list-disc space-y-0.5 pl-5">
                {conversionReviewSteps.map((s) => (
                  <li key={s.workflow_step_id}>
                    <button
                      type="button"
                      onClick={() => openStep(s.candidate_step_id)}
                      className="font-medium underline hover:no-underline"
                    >
                      {s.title}
                    </button>
                    : {missingConversionDetails(s).join(', ')}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {overdueStep && (
            <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
              <span className="font-semibold">Overdue:</span>{' '}
              {overdueStep.linked_sla
                ? `"${(candidate.steps || []).find((x) => x.workflow_step_id === overdueStep.linked_sla.linked_step_id)?.title ?? 'Linked step'}" is still not completed — ${overdueStep.sla_days}-day SLA from reaching "${overdueStep.title}" ended ${overdueStep.days_overdue ?? 0} day(s) ago.`
                : `"${overdueStep.title}" has been in progress for ${overdueStep.days_overdue} day${overdueStep.days_overdue === 1 ? '' : 's'} past its ${overdueStep.sla_days}-day SLA.`}
            </div>
          )}
          <StepIconRow candidate={candidate} selectedStep={selectedStep} onSelect={openStep} />
        </div>

        <div className="flex flex-col rounded-lg border border-gray-200 bg-white p-6">
          <Tabs tabs={TABS} active={tab} onChange={setTab} />
          <div className="mt-4 max-h-[70vh] overflow-y-auto pr-1">
            {tab === 'documents' && <DocumentsTab candidate={candidate} />}
            {tab === 'profile' && <CandidateProfileTab candidate={candidate} />}
            {tab === 'activity' && <ActivityTab candidate={candidate} />}
            {tab === 'sla' && <SlaTab candidate={candidate} />}
          </div>
        </div>
      </div>

      <Modal
        open={stepModalOpen}
        onClose={() => setStepModalOpen(false)}
        title={selectedStep?.title}
        size="xl"
        footer={
          <>
            <Button variant="secondary" onClick={() => setStepModalOpen(false)}>
              <IconClose className="h-4 w-4" />
              Close
            </Button>
            <div id="step-action-bar" className="ml-auto flex flex-wrap justify-end gap-3 empty:hidden" />
          </>
        }
      >
        <StepDetailPanel candidate={candidate} selectedStep={selectedStep} />
      </Modal>
    </div>
  );
}
